import { useQuery } from '@tanstack/react-query';
import { Clock, RefreshCw, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

interface CronJob {
  name: string;
  schedule: string;
  lastRun: string;
  nextRun: string;
  status: 'active' | 'paused' | 'error';
  description: string;
}

export function SchedulerSettingsPage() {
  const { data: cronJobs, isLoading, refetch, isRefetching } = useQuery<CronJob[]>({
    queryKey: ['scheduler'],
    queryFn: async (): Promise<CronJob[]> => {
      try {
        const res: any = await api.settings.get();
        return res.data?.cronJobs ?? getDefaultJobs();
      } catch {
        return getDefaultJobs();
      }
    },
  });

  function getDefaultJobs(): CronJob[] {
    return [
      {
        name: 'Quote Rotation',
        schedule: '0 0 * * *',
        lastRun: new Date(Date.now() - 86400000).toISOString(),
        nextRun: new Date(Date.now() + 86400000).toISOString(),
        status: 'active',
        description: 'Rotates the daily quote at midnight',
      },
      {
        name: 'Telegram Auto-Post',
        schedule: '0 9 * * *',
        lastRun: new Date(Date.now() - 3600000).toISOString(),
        nextRun: new Date(Date.now() + 82800000).toISOString(),
        status: 'active',
        description: 'Posts daily quote to Telegram channel',
      },
      {
        name: 'Stats Aggregation',
        schedule: '*/30 * * * *',
        lastRun: new Date(Date.now() - 1800000).toISOString(),
        nextRun: new Date(Date.now() + 1800000).toISOString(),
        status: 'active',
        description: 'Aggregates view and share statistics',
      },
      {
        name: 'Scheduled Publishing',
        schedule: '*/5 * * * *',
        lastRun: new Date(Date.now() - 300000).toISOString(),
        nextRun: new Date(Date.now() + 300000).toISOString(),
        status: 'active',
        description: 'Publishes scheduled quotes',
      },
    ];
  }

  const statusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'paused':
        return <AlertTriangle size={16} className="text-yellow-500" />;
      case 'error':
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-surface-400" />;
    }
  };

  const statusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-700',
      paused: 'bg-yellow-100 text-yellow-700',
      error: 'bg-red-100 text-red-700',
    };
    return styles[status as keyof typeof styles] ?? 'bg-surface-100 text-surface-600';
  };

  const formatTime = (iso: string) => {
    if (!iso) return '—';
    const date = new Date(iso);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const cronToHuman = (cron: string) => {
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;
    const [min, hour] = parts;
    if (cron === '0 0 * * *') return 'Every day at midnight';
    if (cron === '0 9 * * *') return 'Every day at 9:00 AM';
    if (min.startsWith('*/')) return `Every ${min.slice(2)} minutes`;
    if (hour.startsWith('*/')) return `Every ${hour.slice(2)} hours`;
    if (min !== '*' && hour !== '*') return `At ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
    return cron;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-surface-900">Scheduler</h1>
          <p className="text-surface-500 mt-1">Monitor cron job status and schedules</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <RefreshCw size={18} className={isRefetching ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-3xl" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {cronJobs?.map((job) => (
            <Card key={job.name} variant="glass" className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {statusIcon(job.status)}
                    <h3 className="text-lg font-bold text-surface-900">{job.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusBadge(job.status)}`}>
                      {job.status}
                    </span>
                  </div>
                  <p className="text-sm text-surface-500 mb-3">{job.description}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div className="p-3 rounded-xl bg-surface-50">
                      <p className="text-xs text-surface-400 mb-1">Schedule</p>
                      <p className="font-mono text-surface-800">{job.schedule}</p>
                      <p className="text-xs text-surface-500 mt-1">{cronToHuman(job.schedule)}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-surface-50">
                      <p className="text-xs text-surface-400 mb-1">Last Run</p>
                      <p className="text-surface-800">{formatTime(job.lastRun)}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-surface-50">
                      <p className="text-xs text-surface-400 mb-1">Next Run</p>
                      <p className="text-surface-800">{formatTime(job.nextRun)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card variant="glass" className="p-6">
        <h3 className="text-lg font-bold text-surface-900 mb-3">Cron Reference</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left py-2 px-3 text-surface-500 font-medium">Field</th>
                <th className="text-left py-2 px-3 text-surface-500 font-medium">Values</th>
                <th className="text-left py-2 px-3 text-surface-500 font-medium">Special Characters</th>
              </tr>
            </thead>
            <tbody className="text-surface-700">
              <tr className="border-b border-surface-100">
                <td className="py-2 px-3 font-mono">Minute</td>
                <td className="py-2 px-3">0-59</td>
                <td className="py-2 px-3">* , - /</td>
              </tr>
              <tr className="border-b border-surface-100">
                <td className="py-2 px-3 font-mono">Hour</td>
                <td className="py-2 px-3">0-23</td>
                <td className="py-2 px-3">* , - /</td>
              </tr>
              <tr className="border-b border-surface-100">
                <td className="py-2 px-3 font-mono">Day of Month</td>
                <td className="py-2 px-3">1-31</td>
                <td className="py-2 px-3">* , - /</td>
              </tr>
              <tr className="border-b border-surface-100">
                <td className="py-2 px-3 font-mono">Month</td>
                <td className="py-2 px-3">1-12</td>
                <td className="py-2 px-3">* , - /</td>
              </tr>
              <tr>
                <td className="py-2 px-3 font-mono">Day of Week</td>
                <td className="py-2 px-3">0-6 (Sun=0)</td>
                <td className="py-2 px-3">* , - /</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
